/**
 * The report artifacts this device can actually produce — Z-6's builders, pure and DOM-free.
 *
 * ⚠️ **"Compliance evidence" named data we held and never showed.** The audit chain has been
 * written (hash-linked, PII-free) since the first local merge, and until this module the only place
 * it surfaced was a collapsible list on the Actions screen. A chain nobody can hand to a reviewer
 * is not evidence; the evidence pack below is the handable form — events, the verification verdict,
 * and the head hash, in one file.
 *
 * Three artifacts, all built from the store, all downloadable in any session:
 *
 *   • **Duplicate groups** — one row per cluster in the latest run: which files, which rows, how
 *     strong the weakest link is, what has been decided and what has been merged. The file a RevOps
 *     person forwards to whoever owns the records.
 *   • **Decision log** — every verdict a human recorded, with both rows and when. The answer to
 *     "who said these two were the same person?", minus the who, because local mode has no accounts
 *     — which the column header says rather than hides.
 *   • **Evidence pack** — the audit chain plus its on-the-spot verification. JSON rather than CSV
 *     because its consumers are a reviewer's tooling and the chain's own structure, not a
 *     spreadsheet.
 *
 * What is deliberately NOT here: any monetary figure. ADR-045's rule is a figure without its
 * formula and a tenant-set rate is not shown at all — the Reports screen states the countable
 * inputs and names the missing factor instead of inventing one.
 */
import { sha256, verifyChain, type AuditEvent } from '@dedupr/audit';
import { csvCell, rowIdStripper } from './exportCsv.js';
import type {
  StoredDecision,
  StoredEntity,
  StoredGolden,
  StoredPair,
  StoredRecord,
  StoredSource,
} from './store.js';

export interface ReportInput {
  sources: readonly StoredSource[];
  records: readonly StoredRecord[];
  pairs: readonly StoredPair[];
  entities: readonly StoredEntity[];
  decisions: readonly StoredDecision[];
  golden: readonly StoredGolden[];
}

/**
 * One row per duplicate group in the latest run.
 *
 * `weakest_link_score` rather than an average: a cluster is held together by its least certain
 * pair, and an average over strong links hides the one that should be checked first.
 */
export function buildGroupsReport(input: ReportInput): string {
  const own = rowIdStripper(input.records);
  const byId = new Map(input.records.map((r) => [r.id, r]));
  const nameOf = new Map(input.sources.map((s) => [s.id, s.display_name]));
  const decided = new Map(input.decisions.map((d) => [d.pair_id, d]));
  const liveGolden = input.golden.filter((g) => !g.rolled_back_at);

  const header = [
    'group',
    'files',
    'rows',
    'records',
    'weakest_link_score',
    'decided_pairs',
    'undecided_pairs',
    'merges_applied',
  ];
  const lines = [header.join(',')];

  input.entities.forEach((entity, i) => {
    const members = entity.members.map((id) => byId.get(id)).filter((r): r is StoredRecord => Boolean(r));
    const within = input.pairs.filter(
      (p) => entity.members.includes(p.record_a_id) && entity.members.includes(p.record_b_id)
    );
    const files = [...new Set(members.map((m) => nameOf.get(m.source_id) ?? m.source_id))];
    const decidedCount = within.filter((p) => decided.has(p.id)).length;
    const merges = liveGolden.filter((g) => g.members.every((m) => entity.members.includes(m))).length;
    const weakest = within.length ? Math.min(...within.map((p) => p.score)) : null;

    lines.push(
      [
        csvCell(`group-${i + 1}`),
        csvCell(files.join(' ')),
        csvCell(entity.members.map(own).join(' ')),
        csvCell(entity.members.length),
        /* Blank, never 0: a group with no scored pair has an UNKNOWN weakest link, and `0` would
           read as "certainly different" — the not-knowable-is-not-a-value rule. */
        csvCell(weakest === null ? '' : weakest.toFixed(3)),
        csvCell(decidedCount),
        csvCell(within.length - decidedCount),
        csvCell(merges),
      ].join(',')
    );
  });

  return lines.join('\n') + '\n';
}

/** Every recorded verdict, with both rows named as the visitor knows them. */
export function buildDecisionLog(input: ReportInput): string {
  const own = rowIdStripper(input.records);
  const pairById = new Map(input.pairs.map((p) => [p.id, p]));

  /* `decided_by` is a fixed value and says so: local mode has no accounts, so "you, on this
     device" is the most honest attribution available — a blank would read as "unknown", which is
     less true. */
  const header = ['record_a', 'record_b', 'score', 'verdict', 'decided_at', 'decided_by'];
  const lines = [header.join(',')];

  for (const d of input.decisions) {
    const pair = pairById.get(d.pair_id);
    if (!pair) continue; // A decision for a pair not in the latest run — an earlier run's; skip.
    lines.push(
      [
        csvCell(own(pair.record_a_id)),
        csvCell(own(pair.record_b_id)),
        csvCell(pair.score.toFixed(3)),
        csvCell(d.verdict),
        csvCell(d.decided_at),
        csvCell('you, on this device'),
      ].join(',')
    );
  }

  return lines.join('\n') + '\n';
}

export interface EvidencePack {
  generated_at: string;
  /** What this pack is and is not — carried IN the artifact, because the artifact travels alone. */
  scope: string;
  chain: {
    events: number;
    /** Verified at generation time, on this device — the verdict, not an assertion. */
    verified: boolean;
    verification_detail: string | null;
    head_hash: string | null;
  };
  events: unknown[];
}

/**
 * The audit chain in handable form: events, plus the verification run at generation time.
 *
 * ⚠️ The verification is executed here, not copied from the screen — a pack that asserts "verified"
 * because a UI said so once is an assertion, and the whole point of a hash chain is that the check
 * is cheap enough to run every time.
 */
export async function buildEvidencePack(
  events: readonly Record<string, unknown>[],
  now: () => string = () => new Date().toISOString()
): Promise<string> {
  const verdict = await verifyChain(events as unknown as AuditEvent[], { digest: sha256 });
  const last = events[events.length - 1] as { hash?: string } | undefined;

  const pack: EvidencePack = {
    generated_at: now(),
    scope:
      'Local-mode audit chain from one browser. Events are hash-linked and carry no field values (record ids and actions only). Actor "local:you" means the person at this device — local mode has no accounts.',
    chain: {
      events: events.length,
      verified: verdict.ok,
      verification_detail: verdict.ok ? null : JSON.stringify(verdict),
      head_hash: last?.hash ?? null,
    },
    events: [...events],
  };
  return JSON.stringify(pack, null, 2) + '\n';
}
